import React, { useState } from 'react';
import { PlusCircleOutlined, SlidersOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, InputNumber, Select, Tabs } from 'antd';
import { add, all, always, and, anyPass, compose, cond, dec, dropWhile, equals, filter, gt, inc, join, length, lte, map, or, path, pathOr, prop, propEq, propOr, reduce, replace, subtract, T, takeWhile, isNotEmpty, toString, isNil } from 'ramda';
import { isTrue, notEqual, mapIndexed, isNilOrEmpty } from 'ramda-adjunct';
import { Components } from '@fasstech/flexiget';
import { useDocumentContext } from '../../../../context/Document';
import { BASE_PADDING_DOCUMENT } from '../../../../_CONST';
import { getPagesHeight, getPageWidth, HEIGHT_MAX_A4_WITHOUT_MARGINS } from '../helpers/pages';
import { editorWidth, WIDTH_A4_WITHOUT_MARGINS, WIDTH_LANDSCAPE_WITHOUT_MARGINS } from '../helpers/widget';
import DocumentRules from '../rules/DocumentRules';
import { successNotification } from '../../../../utils/messageMutation';
import colors from '../../../../colors';
import Switch from '../../../../components/Switch';
import DocumentContents from './DocumentContents';
import PaddingInfo from './documentFormTabs/PaddingInfo';
import BackgroundImageSection from './documentFormTabs/BackgroundImageSection';
import DocumentAlerts from './DocumentAlerts';

const DocumentSections = (props) => {
  const { activeKey, setActiveKey, userCanEdit } = props;
  const {
    addWidget,
    addRowWidget,
    removeWidget,
    updateWidget,
    addWidgetRule,
    removeWidgetRule,
    updateWidgetRule,
    getRulesAreValid,
    getConditionsAreValid,
    moveRowWidget,
    editSections,
    addManualBreakPageRule,
    removeManualBreakPageRule,
    updateManualBreakPageRule,
    setIsOpenCreateWidgetModal,
    updatePaddingSection,
    setPendingBackgroundImageSection,
    clearBackgroundImageSection,
    updateBackgroundSizeSection,
    updateBackgroundPositionSection,
    updateBreakPage,
    loading,
    values,
    widgets,
    addRuleSection,
    updateRuleSection,
    removeRuleSection,
    setIsOpenCreateGlobalVariableModal,
    variablesForRules
  } = useDocumentContext();
  const [showSectionOptions, setShowSectionOptions] = useState(false);
  const [showRulesSection, setShowRulesSection] = useState(true);
  const [newSectionIndexAfterSwap, setNewSectionIndexAfterSwap] = useState(1);
  const [selectedValue, setSelectedValue] = useState('');
  const [isRemoveSection, setIsRemoveSection] = useState(false);
  const [sectionIndexToRemove, setSectionIndexToRemove] = useState('');

  const paddingDocument = propOr(BASE_PADDING_DOCUMENT, 'padding', values);
  const pageHeightWithMargins = subtract(
    HEIGHT_MAX_A4_WITHOUT_MARGINS,
    add(prop('top', paddingDocument), prop('bottom', paddingDocument))
  );

  const pages = propOr([], 'pages', values);

  const handleSection = (idx, action, oldIndex) => {
    if (action === 'remove') {
      setSectionIndexToRemove(idx);
      setIsRemoveSection(!isRemoveSection);
    } else {
      if (action === 'add') setActiveKey(`${length(pages)}`);
      editSections(idx, action, oldIndex);
    }
  };

  const tabItems = mapIndexed((section, sectionIndex) => {
    const { Option } = Select;
    const paddingSection = pathOr(BASE_PADDING_DOCUMENT, ['pages', sectionIndex, 'padding'], values);

    const widgetsHeader = pathOr([], ['pages', sectionIndex, 'header'], values);
    const widgetsSection = pathOr([], ['pages', sectionIndex, 'widgets'], values);
    const widgetsFooter = pathOr([], ['pages', sectionIndex, 'footer'], values);
    const firstPage = takeWhile(propEq(false, 'breakPage'))(widgetsSection);
    const nextPagesWidgets = dropWhile(propEq(false, 'breakPage'))(widgetsSection);

    const nextPages = reduce(
      (acc, b) => {
        const newIndex = cond([
          [equals(true), always(acc.length)],
          [() => equals(acc.length, 0), always(0)],
          [T, always(acc.length - 1)]
        ])(prop('breakPage', b));
        if (prop('breakPage', b) || !acc[newIndex]) acc[newIndex] = [];
        acc[newIndex].push(b);
        return acc;
      },
      [],
      nextPagesWidgets
    );
    const pagesOnSection = [firstPage, ...nextPages];
    const pagesHeight = getPagesHeight(
      widgetsHeader,
      pagesOnSection,
      widgetsFooter,
      widgets,
      paddingSection
    );

    const underMaxPageHeight = map(gt(pageHeightWithMargins))(
      pagesHeight
    );
    const isUnderMaxPageHeight = all(equals(true))(underMaxPageHeight);
    const pagesExceedingMaxHeight = compose(
      filter(lte(0)),
      map((isUnderHeightLimit, pageIndex) =>
        equals(false, isUnderHeightLimit) ? pageIndex + 1 : undefined)
    )(underMaxPageHeight);

    const pageWidth = getPageWidth(widgetsSection, widgets, paddingSection);
    const pageWidthWithMargins = subtract(
      editorWidth(propOr('portrait', 'orientation', values)),
      add(prop('right', paddingDocument), prop('left', paddingDocument))
    );

    const documentWidthByOrientation = equals(propOr('portrait', 'orientation', values), 'portrait') ? WIDTH_A4_WITHOUT_MARGINS : WIDTH_LANDSCAPE_WITHOUT_MARGINS;
    const widthRowGoesBeyondsPage = gt(pageWidth, documentWidthByOrientation) || gt(pageWidthWithMargins, documentWidthByOrientation);
    const hasBreakPage = path(['pages', sectionIndex, 'breakPage'], values);
    const canRemoveSection = and(userCanEdit, or(
      notEqual(sectionIndex, 0),
      gt(pages.length, 1)
    ));
    const showWarningBeyondsPage = or(
      and(!isUnderMaxPageHeight, !hasBreakPage),
      isTrue(widthRowGoesBeyondsPage)
    );
    const textPagesGoesBeyonds = compose(
      replace(/, ([^,]*)$/, ' et $1'),
      join(', ')
    )(pagesExceedingMaxHeight);

    const isMovingOrSwapingSection = or(equals(selectedValue, 'moveSectionBefore'), equals(selectedValue, 'swapSections'));
    const editSection = (sectionIndex) => {
      const addOrRemoveSection = (sectionIndex, selectedValue) => {
        editSections(sectionIndex, selectedValue);
        setShowSectionOptions(false);
      };
      const newSection = newSectionIndexAfterSwap - 1;

      cond([
        [anyPass([equals('addSectionAfter'), equals('addSectionBefore')]), () => addOrRemoveSection(sectionIndex, selectedValue)],
        [equals('duplicateSection'), () => editSections(sectionIndex, 'duplicateSection')],
        [equals('swapSections'), () => editSections(sectionIndex, 'swapSections', newSection)],
        [equals('moveSectionBefore'), () => editSections(sectionIndex, 'moveSectionBefore', newSection)]
      ])(selectedValue);

      const currentSectionIndex = sectionIndex + 1;
      const message = cond([
        [equals('addSectionBefore'), always(`Une section a été ajoutée avant la section n°${currentSectionIndex}`)],
        [equals('addSectionAfter'), always(`Une section a été ajoutée après la section n°${currentSectionIndex}`)],
        [equals('duplicateSection'), always(`La section n°${currentSectionIndex} a bien été dupliquée`)],
        [equals('swapSections'), always(`La section n°${currentSectionIndex} a bien été échangée avec la section n°${newSectionIndexAfterSwap}`)],
        [equals('moveSectionBefore'), always(`La section n°${currentSectionIndex} a bien été déplacée avant la section n°${newSectionIndexAfterSwap}`)]
      ])(selectedValue);

      successNotification(message);
      setSelectedValue('');
    };
    const rulesSection = path(['pages', sectionIndex, 'rules'], values);
    return {
      key: toString(sectionIndex),
      className: `section-${sectionIndex}`,
      label: (
        <span className="text-flexi-text font-semibold text-lg">{`Section ${sectionIndex + 1}`}</span>
      ),
      closable: canRemoveSection,
      children: (
        <>
          <Components.Tooltip title="Options de la section" className="float-right mr-2 mt-[-1.2rem]">
            <button
              className={`show-section-options-${sectionIndex} text-flexi-primary-color font-semibold`}
              onClick={() => setShowSectionOptions(!showSectionOptions)}
            >
              {showSectionOptions ? '-' : '+'} Options
            </button>
          </Components.Tooltip>

          {isRemoveSection && (
            <Alert
              type="error"
              showIcon
              message={`Suppression de la section ${inc(sectionIndexToRemove)}`}
              description={(
                <div className="flex flex-col">
                  Êtes-vous sûr de vouloir la supprimer ?
                  <div className="inline-flex space-x-2 justify-end">
                    <Button
                      type="text"
                      onClick={() => {
                        setSectionIndexToRemove('');
                        setIsRemoveSection(false);
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        setSectionIndexToRemove('');
                        editSections(sectionIndexToRemove, 'remove');
                        const updatedKey = equals('0', sectionIndexToRemove) ? sectionIndexToRemove : dec(sectionIndexToRemove);
                        setActiveKey(`${updatedKey}`);
                        setIsRemoveSection(false);
                      }}
                    >Supprimer
                    </Button>
                  </div>
                </div>
              )}
            />
          )}
          {showSectionOptions && (
            <div className="flex flex-col">
              {userCanEdit && (
                <div className="inline-flex mb-4 space-x-4">
                  <Components.Tooltip title="Modifications de la section" className="w-2/3">
                    <Select
                      placeholder="Sélectionnez une action"
                      className={`select-options-section-${sectionIndex}`}
                      onChange={setSelectedValue}
                      value={selectedValue}
                      size="small"
                    >
                      <Option value="swapSections">Échanger avec la section</Option>
                      <Option value="moveSectionBefore">Déplacer avant la section</Option>
                      <Option value="duplicateSection">Dupliquer cette section</Option>
                      <Option value="addSectionBefore">Ajouter une section avant</Option>
                      <Option value="addSectionAfter">Ajouter une section après</Option>
                    </Select>
                  </Components.Tooltip>

                  {isMovingOrSwapingSection && (
                    <Components.Tooltip title="Section n°">
                      <InputNumber
                        className="section-opt-input-nb" min={1} max={pages.length}
                        size="small" defaultValue={1} onChange={setNewSectionIndexAfterSwap}
                      />
                    </Components.Tooltip>
                  )}

                  <Button
                    onClick={() => editSection(sectionIndex)}
                    size="small"
                    className={`btn-options-section-${sectionIndex}`}
                    disabled={or(isNilOrEmpty(selectedValue), and(isMovingOrSwapingSection, isNil(newSectionIndexAfterSwap)))}
                  >
                    Ok
                  </Button>
                </div>
              )}
              {(userCanEdit || (!userCanEdit && isNotEmpty(rulesSection))) && (
                <div className={`mb-2 flex ${isNotEmpty(rulesSection) ? 'items-start' : 'items-center'}`}>
                  <Badge
                    dot={isNotEmpty(rulesSection)}
                    color={isNotEmpty(rulesSection) ? colors['flexi-primary-color'] : undefined}
                    className="!mr-2"
                  >
                    <SlidersOutlined
                      rotate={90}
                      onClick={() => isNotEmpty(rulesSection) && setShowRulesSection(!showRulesSection)}
                    />
                  </Badge>
                  {showRulesSection ? (
                    <DocumentRules
                      variables={variablesForRules}
                      rules={rulesSection}
                      rulesAreValid={getRulesAreValid(rulesSection)}
                      getConditionsAreValid={getConditionsAreValid}
                      onAdd={() => addRuleSection(sectionIndex)}
                      onRemove={removeRuleSection(sectionIndex)}
                      onUpdate={updateRuleSection(sectionIndex)}
                      classNameButton="!mt-0"
                      userCanEdit={userCanEdit}
                      setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
                    />
                  ) : <p className="italic">{'L\'affichage de la section est conditionnée'}</p>}
                </div>
              )}
              <div className="mb-2">
                <PaddingInfo
                  updatePadding={updatePaddingSection(sectionIndex)}
                  element={section}
                  userCanEdit={userCanEdit}
                />
              </div>
              <div className="mb-2">
                <BackgroundImageSection
                  section={section}
                  setPendingBackgroundImage={setPendingBackgroundImageSection(sectionIndex)}
                  clearBackgroundImage={() => clearBackgroundImageSection(sectionIndex)}
                  updateBackgroundSize={updateBackgroundSizeSection(sectionIndex)}
                  updateBackgroundPosition={updateBackgroundPositionSection(sectionIndex)}
                  userCanEdit={userCanEdit && !loading}
                />
              </div>
              <Switch
                className="mb-2"
                checked={hasBreakPage}
                onChange={(v) => updateBreakPage(sectionIndex, v)}
                disabled={!userCanEdit}
                onClick={() => userCanEdit && updateBreakPage(sectionIndex, !hasBreakPage)}
                label="Saut de page automatique"
              />
            </div>
          )}
          <div className="mb-2">
            {showWarningBeyondsPage && (
              <DocumentAlerts
                pagesNumberGoesBeyonds={pagesExceedingMaxHeight}
                textPagesGoesBeyonds={textPagesGoesBeyonds}
                widthRowGoesBeyondsPage={widthRowGoesBeyondsPage}
                hasBreakPage={hasBreakPage}
              />
            )}
          </div>
          <DocumentContents
            title="En-tête"
            type="header"
            content={widgetsHeader}
            sectionIndex={sectionIndex}
            hasSectionBreakPage={hasBreakPage}
            addWidget={addWidget}
            addRowWidget={addRowWidget}
            removeWidget={removeWidget}
            updateWidget={updateWidget}
            addWidgetRule={addWidgetRule}
            removeWidgetRule={removeWidgetRule}
            updateWidgetRule={updateWidgetRule}
            getRulesAreValid={getRulesAreValid}
            getConditionsAreValid={getConditionsAreValid}
            moveRowWidget={moveRowWidget}
            addManualBreakPageRule={addManualBreakPageRule}
            removeManualBreakPageRule={removeManualBreakPageRule}
            updateManualBreakPageRule={updateManualBreakPageRule}
            setIsOpenCreateWidgetModal={setIsOpenCreateWidgetModal}
            {...props}
          />
          <DocumentContents
            title="Contenu"
            type="widgets"
            content={widgetsSection}
            sectionIndex={sectionIndex}
            hasSectionBreakPage={hasBreakPage}
            addWidget={addWidget}
            addRowWidget={addRowWidget}
            removeWidget={removeWidget}
            updateWidget={updateWidget}
            addWidgetRule={addWidgetRule}
            removeWidgetRule={removeWidgetRule}
            updateWidgetRule={updateWidgetRule}
            getRulesAreValid={getRulesAreValid}
            getConditionsAreValid={getConditionsAreValid}
            moveRowWidget={moveRowWidget}
            addManualBreakPageRule={addManualBreakPageRule}
            removeManualBreakPageRule={removeManualBreakPageRule}
            updateManualBreakPageRule={updateManualBreakPageRule}
            setIsOpenCreateWidgetModal={setIsOpenCreateWidgetModal}
            {...props}
          />
          <DocumentContents
            title="Pied de page"
            type="footer"
            content={widgetsFooter}
            sectionIndex={sectionIndex}
            hasSectionBreakPage={hasBreakPage}
            addWidget={addWidget}
            addRowWidget={addRowWidget}
            removeWidget={removeWidget}
            updateWidget={updateWidget}
            addWidgetRule={addWidgetRule}
            removeWidgetRule={removeWidgetRule}
            updateWidgetRule={updateWidgetRule}
            getRulesAreValid={getRulesAreValid}
            getConditionsAreValid={getConditionsAreValid}
            moveRowWidget={moveRowWidget}
            addManualBreakPageRule={addManualBreakPageRule}
            removeManualBreakPageRule={removeManualBreakPageRule}
            updateManualBreakPageRule={updateManualBreakPageRule}
            setIsOpenCreateWidgetModal={setIsOpenCreateWidgetModal}
            {...props}
          />
        </>
      )
    };
  })(pages);

  return (
    <div>
      <Tabs
        type="editable-card"
        onEdit={handleSection}
        className="tabs-doc-pages"
        addIcon={<PlusCircleOutlined />}
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        hideAdd={!userCanEdit}
        items={tabItems}
      />
    </div>
  );
};

export default DocumentSections;
